// Pure, view-agnostic helpers for the reference DRILL-DOWN page (SpinReferenceDetailPage). Kept out of
// spinMetrics.logic.ts so the drill-down can evolve independently of the whole-set reference roll-up.
import type { RefDrilldown } from '../../../Generated/RefDrilldown.ts'
import type { RefMember } from '../../../Generated/RefMember.ts'
import type { RefSpin } from '../../../Generated/RefSpin.ts'
import type { SpinReferenceKind } from '../../../Repository/Admin/AdminSpinRepository.js'

/** Human label per reference kind, for headings and column titles. */
export const REF_KIND_LABEL: Record<SpinReferenceKind, string> = {
  NCBI_ID: 'NCBI accession',
  NCBI_TAXID: 'NCBI taxon id',
  BOLD_BIN: 'BOLD bin'
}

export const refKindLabel = (kind: string): string => REF_KIND_LABEL[kind as SpinReferenceKind] ?? kind

/** True for the reference kinds whose id was read from an NCBI blast row (so identity columns apply). */
export const isNcbiKind = (kind: string): boolean => kind === 'NCBI_ID' || kind === 'NCBI_TAXID'

/**
 * `NCBI_TAXID` seeds off an UNINDEXED column (`raw_blast_results.staxids`): measured ~4.4s warm / ~40s
 * cold per statement, and the drill-down runs three — i.e. up to ~2 minutes cold, versus well under a
 * second for the other kinds. So this kind must NEVER auto-fetch; the page requires an explicit load.
 */
/**
 * Whether the WHOLE-SET reference concordance query is slow for this kind, i.e. must be opt-in rather
 * than fired on a dropdown change. True only for `NCBI_TAXID`: that query fans `staxids` out across
 * EVERY current member, driven by `otu_uuid`, so no index on `staxids` can help it.
 *
 * Deliberately whole-set-only. The single-id DRILL-DOWN is fast for every kind since migration V90
 * added the expression + partial indexes on `raw_blast_results.staxids` (seed ~4.4s → ~48ms, heaviest
 * composed statement ~124ms), so the drill-down page fetches on mount for taxon ids too. Do not reuse
 * this predicate there.
 */
export const isSlowWholeSetRefKind = (kind: string): boolean => kind === 'NCBI_TAXID'

/**
 * Which blast source the identity columns (`hitPercentage` / `queryCoverage` / `gradePercentage`) were
 * read from — the `NCBI` row for the NCBI kinds, the `BOLD` row for BOLD_BIN. Always name the source in
 * the label: a bare "hit %" hides that this is the match to THIS reference id from THAT source, and a
 * member carrying only the other source's id legitimately shows no identity at all.
 */
export const identitySource = (kind: string): string => (isNcbiKind(kind) ? 'NCBI' : 'BOLD')

/** The cross-source id shown next to a member: BOLD bin for the NCBI kinds, NCBI accession for BOLD_BIN. */
export const otherRefLabel = (kind: string): string => (isNcbiKind(kind) ? 'BOLD bin' : 'NCBI accession')

/** Only a valid reference-kind path segment may reach the API; anything else is a bad URL. */
export const parseRefKind = (raw: string | undefined): SpinReferenceKind | null =>
  raw && raw in REF_KIND_LABEL ? (raw as SpinReferenceKind) : null

/**
 * Path into the drill-down page. The id lands in a PATH segment and BOLD keys carry a colon while
 * accessions carry a dot, so it must be percent-encoded here (and is decoded by [decodeRefIdParam]).
 */
export const refDetailPath = (kind: SpinReferenceKind, refId: string, search = ''): string => {
  const qs = search.startsWith('?') ? search : search ? `?${search}` : ''
  return `/admin/spin/reference/${kind}/${encodeURIComponent(refId)}${qs}`
}

/**
 * Read the `:refId` path param. react-router already percent-decodes params, so this is normally a
 * pass-through; the extra decode is a belt-and-braces guard for doubly-encoded links and never throws.
 */
export const decodeRefIdParam = (raw: string | undefined): string => {
  if (!raw) return ''
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

/** Percentage-ish value → 1-decimal string, or an em dash when the source row had none. */
export const pct = (v: number | undefined | null): string => (v == null ? '—' : `${v.toFixed(1)}%`)

/** Plain integer → locale string, em dash when absent. */
export const num = (v: number | undefined | null): string => (v == null ? '—' : v.toLocaleString())

/** `hitPercentageMin`–`hitPercentageMax` as one KPI value; collapses to a single number when equal. */
export const identityRange = (min: number | undefined, max: number | undefined): string => {
  if (min == null && max == null) return '—'
  if (min == null || max == null) return pct(min ?? max)
  if (Math.abs(min - max) < 0.05) return pct(min)
  return `${min.toFixed(1)}–${max.toFixed(1)}%`
}

/**
 * Truncation disclosure for the SPIN list. The backend caps `spins` (largest-first) while `spinCount`
 * keeps the TRUE total — on real data the worst accession spans 2,397 SPINs, so a capped table rendered
 * as if complete is the exact failure this page exists to prevent. Returns null when nothing was cut.
 */
export const spinTruncationNote = (d: Pick<RefDrilldown, 'truncated' | 'returnedSpinCount' | 'spinCount'>): string | null => {
  const shown = d.returnedSpinCount
  if (!d.truncated && shown >= d.spinCount) return null
  return `Showing ${shown.toLocaleString()} of ${d.spinCount.toLocaleString()} SPINs, largest first — the list is capped.`
}

/** Same disclosure per SPIN: `members.length < membersWithRef` ⇒ the member list was capped. */
export const memberTruncationNote = (s: Pick<RefSpin, 'members' | 'membersWithRef'>): string | null => {
  const shown = (s.members ?? []).length
  if (shown >= s.membersWithRef) return null
  return `Showing ${shown.toLocaleString()} of ${s.membersWithRef.toLocaleString()} members carrying this id.`
}

/** Identity at or above this is "near-perfect" — splitting despite it is the suspicious cohort. */
export const NEAR_PERFECT_IDENTITY = 99.5

/** One "what to check" row: an explanation candidate, in the order a reader should work down them. */
export interface ExplanationCheck {
  key: string
  label: string
  detail: string
  /** True when the drill-down data actually shows this signal — render it as the live finding. */
  flagged: boolean
  /** Short evidence string for the flagged case ('' when not flagged). */
  evidence: string
}

const distinct = <T>(values: Array<T | undefined>): T[] => [...new Set(values.filter((v): v is T => v != null && v !== undefined))]

/** Predecessor SPIN ids shared by more than one of the returned SPINs — an earlier run split this id. */
export const sharedPredecessors = (spins: RefSpin[]): string[] => {
  const counts = new Map<string, number>()
  spins.forEach((s) => {
    // A predecessor listed twice within one SPIN must not look "shared", so de-dup per SPIN first.
    distinct(s.predecessorSpinIds ?? []).forEach((p) => counts.set(p, (counts.get(p) ?? 0) + 1))
  })
  return [...counts.entries()]
    .filter(([, n]) => n > 1)
    .map(([p]) => p)
    .sort()
}

/** Every member across the returned SPINs, flattened (both collections may be omitted by the backend). */
export const allMembers = (spins: RefSpin[]): RefMember[] => spins.flatMap((s) => s.members ?? [])

/** Distinct assignment DAYS across the members — several means the split is partly a history artifact. */
export const assignmentDays = (spins: RefSpin[]): string[] =>
  distinct(allMembers(spins).map((m) => m.assignedAt?.slice(0, 10))).sort()

/**
 * The ordered diagnostic checklist for "why is this id split?". Order is deliberate: cheap, fully
 * explanatory causes first (a SPIN is per-amplicon, so differing amplicons explain the split outright),
 * then structural ones, then the ones that only hint. `flagged` says the data shows the signal.
 */
export const explanationChecks = (d: RefDrilldown): ExplanationCheck[] => {
  const spins = d.spins ?? []
  const amplicons = distinct(spins.map((s) => s.amplicon))
  const lengths = distinct(spins.map((s) => s.coreSequenceLength))
  const singletons = spins.filter((s) => s.currentMemberCount === 1)
  const shared = sharedPredecessors(spins)
  const days = assignmentDays(spins)
  const species = d.distinctSpecies ?? []
  const worstIdentity = d.hitPercentageMin

  return [
    {
      key: 'amplicon',
      label: 'Differing amplicon',
      detail:
        'A SPIN is per-amplicon, so the same id seen on two amplicons MUST land in different clusters. If the amplicons differ, the split is fully explained and nothing below matters.',
      flagged: amplicons.length > 1,
      evidence: amplicons.length > 1 ? `${amplicons.length} amplicons: ${amplicons.join(', ')}` : ''
    },
    {
      key: 'coreLength',
      label: 'Differing core sequence length',
      detail:
        'Clusters built on visibly different core lengths are different sequences, not a clustering mistake. Length only — trigram similarity was measured on these sequences and cannot tell same-species from different-phylum apart.',
      flagged: lengths.length > 1,
      evidence: lengths.length > 1 ? `${lengths.length} distinct lengths: ${Math.min(...lengths)}–${Math.max(...lengths)} bp` : ''
    },
    {
      key: 'singletons',
      label: 'Singleton fragmentation',
      detail:
        'SPINs holding exactly one current member are fragments, not competing clusters. A fan-out made mostly of singletons is a sensitivity problem, not a merge conflict.',
      flagged: singletons.length > 0,
      evidence: singletons.length > 0 ? `${singletons.length} of ${spins.length} shown SPINs are singletons` : ''
    },
    {
      key: 'identity',
      label: 'Weak identity to the reference',
      detail: `A promiscuous reference hit is a property of the reference, not of SPIN. When the worst member identity is well below ${NEAR_PERFECT_IDENTITY}%, splitting is the correct answer; at or above it, SPIN split despite a near-perfect match and that is the suspicious case.`,
      flagged: worstIdentity != null && worstIdentity < NEAR_PERFECT_IDENTITY,
      evidence: worstIdentity != null && worstIdentity < NEAR_PERFECT_IDENTITY ? `worst NCBI identity ${pct(worstIdentity)}` : ''
    },
    {
      key: 'assignedAt',
      label: 'Differing assignment timestamps',
      detail:
        'Membership is latest-wins by assigned_at. Members assigned on different days may simply never have been re-clustered together — a history artifact rather than a live disagreement.',
      flagged: days.length > 1,
      evidence: days.length > 1 ? `${days.length} assignment days: ${days[0]} … ${days[days.length - 1]}` : ''
    },
    {
      key: 'predecessors',
      label: 'Shared predecessors',
      detail:
        'Two of these SPINs descending from the same predecessor means an earlier run actively split this id — look at that run rather than at the current set.',
      flagged: shared.length > 0,
      evidence: shared.length > 0 ? `${shared.length} predecessor(s) shared by >1 SPIN` : ''
    },
    {
      key: 'species',
      label: 'Same resolved species across SPINs',
      detail:
        'If everything below resolves to one species and none of the above fired, this is the strongest "should have been one cluster" signal left.',
      flagged: species.length === 1 && d.spinCount > 1,
      evidence: species.length === 1 && d.spinCount > 1 ? `all resolve to ${species[0]}` : ''
    }
  ]
}
